import { useGeld } from '../../lib/geld';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { CalendarCheck, Clock, Coins, Thermometer } from 'lucide-react';
import { api } from '../../lib/api';
import {
  KpiCard, KpiRow, ChartCard, TabLoading, TabError, EmptyChart, CHART, ANIM, uur1,
  TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE, TOOLTIP_ITEM_STYLE, TOOLTIP_CURSOR, GRID_STROKE, AXIS_PROPS, LEGEND_PROPS,
  RATE_LABELS, RATE_COLORS,
} from './StatsShared';

const hoursFmt = uur1;

export function UrenTab({ year }: { year: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats-uren', year],
    queryFn: () => api.stats.uren(year),
  });
  const { bedrag, label } = useGeld();
  const m = (b: number, y: number) => bedrag(b, `${y}-06-15`) ?? b;

  if (isLoading) return <TabLoading />;
  if (error || !data) return <TabError error={error} />;

  const perPeriodData = data.periods.map((p) => ({
    name: `P${p.period}`,
    Gewerkt: Math.round(p.gewerkt * 10) / 10,
    Verlof: Math.round(p.verlof * 10) / 10,
    Ziekte: Math.round(p.ziekte * 10) / 10,
    Contract: data.contract_uren_per_periode,
  }));

  const rateData = data.rate_totals
    .filter((r) => r.hours > 0.01)
    .map((r) => ({
      name: RATE_LABELS[r.rate_type] ?? r.rate_type,
      value: Math.round(r.hours * 10) / 10,
      fill: RATE_COLORS[r.rate_type] ?? '#64748b',
    }));

  return (
    <div className="space-y-4 sm:space-y-6">
      <KpiRow>
        <KpiCard icon={Clock} label="Gewerkte uren" kpi={data.kpis.gewerkt} fmt="hours" prevYear={year - 1} />
        <KpiCard icon={CalendarCheck} label="Verlof opgenomen" kpi={data.kpis.verlof} fmt="hours" prevYear={year - 1} />
        <KpiCard icon={Thermometer} label="Ziektedagen" kpi={data.kpis.ziekte} fmt="int" prevYear={year - 1} inverseDelta />
        <KpiCard
          icon={Coins}
          label={`${label} uit diensten`}
          kpi={{
            value: m(data.kpis.bruto.value, year),
            prev: m(data.kpis.bruto.prev, year - 1),
            delta: m(data.kpis.bruto.value, year) - m(data.kpis.bruto.prev, year - 1),
          }}
          fmt="money"
          prevYear={year - 1}
        />
      </KpiRow>

      <ChartCard title="Uren per periode" subtitle="Gewerkt, verlof en ziekte opgeteld">
        <ResponsiveContainer>
          <BarChart data={perPeriodData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="name" {...AXIS_PROPS} />
            <YAxis {...AXIS_PROPS} width={36} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} formatter={hoursFmt} />
            <Legend {...LEGEND_PROPS} />
            <Bar dataKey="Gewerkt" stackId="u" fill={CHART.main} isAnimationActive={false} />
            <Bar dataKey="Verlof" stackId="u" fill={CHART.verlof} isAnimationActive={false} />
            <Bar dataKey="Ziekte" stackId="u" fill={CHART.ziek} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid gap-4 sm:gap-6 md:grid-cols-2 [&>*]:min-w-0">
        <ChartCard title="Uren per tarief" subtitle="Hoeveel uur je op elk toeslagpercentage werkte" height={300}>
          {rateData.length === 0 ? (
            <EmptyChart>Nog geen gewerkte uren in {year}.</EmptyChart>
          ) : (
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={rateData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="55%"
                  outerRadius="80%"
                  paddingAngle={1}
                  stroke="#14181E" isAnimationActive={false}
                >
                  {rateData.map((r, i) => (
                    <Cell key={i} fill={r.fill} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} formatter={hoursFmt} />
                <Legend {...LEGEND_PROPS} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Uren per weekdag" height={300}>
          <ResponsiveContainer>
            <BarChart data={data.day_of_week} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="day" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} width={36} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} formatter={hoursFmt} />
              <Bar dataKey="hours" name="Uren" fill={CHART.main} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
